import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { PageHeader } from "@/components/shared/page-header";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";

interface RosterMember {
  name: string;
  title: string;
  ownerId?: string;
  image?: string;
}

function getInitials(name: string) {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function TeamRosterPage({
  team,
  members,
  children,
}: {
  team: string;
  members: RosterMember[];
  children?: ReactNode;
}) {
  return (
    <div className="space-y-6 p-6">
      <PageHeader title={team} description="Team roster" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {members.map((member) => {
          const card = (
            <Card
              className={
                member.ownerId
                  ? "transition-colors hover:border-brand-gold/50 hover:bg-muted/30"
                  : undefined
              }
            >
              <CardContent className="flex items-center gap-3">
                <Avatar className="h-16 w-16 shrink-0">
                  {member.image && <AvatarImage src={member.image} alt={member.name} />}
                  <AvatarFallback className="bg-brand-gold/15 text-brand-gold text-lg font-semibold">
                    {getInitials(member.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="font-serif text-sm font-medium text-foreground truncate">
                    {member.name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{member.title}</p>
                </div>
              </CardContent>
            </Card>
          );
          if (!member.ownerId) {
            return <div key={member.name}>{card}</div>;
          }
          return (
            <Link
              key={member.name}
              to="/owner-metrics/$ownerId"
              params={{ ownerId: member.ownerId }}
            >
              {card}
            </Link>
          );
        })}
      </div>
      {children}
    </div>
  );
}
